import { Box } from "@mui/material"
import { BulkDeleteDialog } from "@/components/ui/bulk-delete-dialog"
import { formatCurrency } from "@crm-src/lib/utils"

interface Invoice {
  /** A quote mirrored from a reseller's portal request (2026-09-15): deleting
   * it removes the reseller's own row too, so the dialog must say so. */
  locked?: boolean
  bill_to_reseller?: { id: number; name: string; code?: string } | null
  id: string | number
  invoice_number: string
  total_amount: number
  customer_company_name?: string
  customer_name?: string
  payment_status: string
}

interface BulkDeleteInvoiceModalProps {
  isOpen: boolean
  onClose: () => void
  selectedInvoices: Invoice[]
  onConfirmDelete?: (invoiceIds: string[]) => Promise<void>
  documentType?: "invoice" | "quote"
}

export function BulkDeleteInvoiceModal({
  isOpen,
  onClose,
  selectedInvoices,
  onConfirmDelete,
  documentType = "invoice",
}: BulkDeleteInvoiceModalProps) {
  const isQuote = documentType === "quote"
  const labels = isQuote
    ? { singular: "Quote", plural: "Quotes" }
    : { singular: "Invoice", plural: "Invoices" }
  const noun = documentType
  // Any reseller request in the selection leaves the reseller's portal too.
  const resellerRequests = selectedInvoices.filter((inv) => inv.locked && inv.bill_to_reseller)

  return (
    <BulkDeleteDialog
      isOpen={isOpen}
      onClose={onClose}
      items={selectedInvoices}
      entityLabel={labels}
      subtitle="They are marked as deleted and can be restored"
      cascadingWarning={
        resellerRequests.length > 0
          ? `${resellerRequests.map((inv) => inv.invoice_number).join(", ")} ${resellerRequests.length === 1 ? "was" : "were"} requested by a reseller on XOP Anytime — deleting removes ${resellerRequests.length === 1 ? "it" : "them"} from the reseller's portal too. The rest are marked as deleted and can be restored; files are preserved.`
          : `This will mark all selected ${noun}s as deleted. They can be restored if needed. Associated files will be preserved.`
      }
      renderItem={(inv) => (
        <>
          {inv.invoice_number}
          <Box component="span" sx={{ color: "error.main" }}>
            {" "}
            — {formatCurrency(inv.total_amount)}
            {inv.customer_company_name || inv.customer_name
              ? ` — ${inv.customer_company_name || inv.customer_name}`
              : ""}
          </Box>
        </>
      )}
      onConfirm={async (ids) => {
        if (onConfirmDelete) await onConfirmDelete(ids)
      }}
    />
  )
}
